<template>
  <div v-if="entities && showSearch" v-click-outside="clickedOutsideSearchResult" class="instant-result">
    <template v-if="showDidYouMean">
      <div class="instant-result__did-you-mean">
        {{ tStatic('searchResultDidYouMean') }}:
        <span
          v-for="item of entities.items.didYouMean"
          :key="`did-you-mean-${item}`"
          class="instant-result__did-you-mean-item"
          v-html="searchResultHtml(item)">
        </span>
      </div>
    </template>

    <div v-if="emptySearchResult" class="instant-result__no-results-search">
      <div>{{ tStatic('searchAgain') }}</div>
      <div class="instant-result__no-results-search-description">
        {{ tStatic('searchAgainDescription') }}
        <hr v-if="lastSearchQuery" class="instant-result__cai-divider" aria-hidden="true">
        <div v-if="lastSearchQuery" class="instant-result__cai-row">
          <span>{{ tStatic('noInstantSearchResultAskCaiIntroText') }}</span>
          <button
            class="instant-result__ask-cai-btn"
            type="button"
            :disabled="isCaiPending"
            @click="openCAI">
            <Chat width="24" height="24" aria-hidden="true"></Chat>
            {{ tStatic('noInstantSearchResultAskCaiButtonText') }}
            <ArrowRight width="16" height="16" aria-hidden="true"></ArrowRight>
          </button>
        </div>
      </div>
    </div>

    <template v-if="!emptySearchResult">
      <oie-instant-search-result-section
        :translations="{ title: tStatic('items') }"
        endpoint="items"
        :data="entities.items"
        class="instant-result__products"
        :entities="entities"
        :use-image="true"></oie-instant-search-result-section>

      <div class="instant-result__aside">
        <oie-instant-search-result-section
          :translations="{ title: tStatic('models') }"
          endpoint="models"
          :entities="entities"
          :data="entities.models"></oie-instant-search-result-section>

        <oie-instant-search-result-section
          :translations="{ title: tStatic('categories') }"
          endpoint="categories"
          :data="entities.categories"
          :entities="entities"
          class="instant-result__aside-categories"></oie-instant-search-result-section>

        <oie-instant-search-superuser-section
          v-if="hasSuperUserResults"
          :items="entities.items.extraResults"></oie-instant-search-superuser-section>
      </div>
    </template>
  </div>
</template>

<script>
import { createSearchResultQueryParams, escapeHtml } from '@/core/search-service'
import { mapGetters } from 'vuex'

export default {
  data: () => ({
    entities: null,
    showSearch: false,
    isCaiPending: false
  }),
  computed: {
    ...mapGetters('ui', ['lastSearchQuery']),
    // Only superusers get these: the item's Business Central rows, shown in a section of their own.
    hasSuperUserResults() {
      return !!(this.entities.items.extraResults && this.entities.items.extraResults.length)
    },
    emptySearchResult() {
      return (this.entities.items.totalCount === null || this.entities.items.totalCount === 0) && (this.entities.categories.totalCount === null || this.entities.categories.totalCount === 0) && (this.entities.models.totalCount === null || this.entities.models.totalCount === 0) && !this.hasSuperUserResults
    },
    showDidYouMean() {
      return this.entities.items.didYouMean && this.entities.items.didYouMean.length > 0 && (this.entities.items.totalCount === null || this.entities.items.totalCount === 0) && (this.entities.categories.totalCount === null || this.entities.categories.totalCount === 0) && (this.entities.models.totalCount === null || this.entities.models.totalCount === 0)
    }
  },
  mounted() {
    this.subscribe('instantsearch.entities.changed', (entities) => {
      this.entities = entities
    })
    this.subscribe('search.show', value => {
      this.showSearch = value
    })
    this.subscribe('chat.response.complete', () => {
      this.isCaiPending = false
    })
  },
  methods: {
    clickedOutsideSearchResult(e) {
      const searchBox = document.querySelector('.instant-search__input')

      if(e.target !== searchBox && document.activeElement !== searchBox) {
        this.showSearch = false
        this.entities = null

        this.publish('instantsearch.clickoutside.clear.search')
      }
    },
    searchResultUrl(itemId) {
      const params = createSearchResultQueryParams(itemId, this.entities, this.endpoint)
      return `${this.clientModel.searchResultPageUrl}?${params}`
    },
    // Whether the index highlighted this suggestion. A display test, not a safety one - the
    // rendering below is what makes it safe.
    isHtml(item) {
      const htmlRegex = /<\/?[a-z][\s\S]*>/i
      return htmlRegex.test(item)
    },
    searchResultHtml(item) {
      const text = escapeHtml(item)

      if (this.isHtml(item)) {
        return text
      } else {
        // searchResultUrl goes through qs.stringify, so the href is encoded; only the text is not.
        return `<a class="instant-result__did-you-mean-item" href="${this.searchResultUrl(item)}">${text}</a>`
      }
    },
    openCAI() {
      this.isCaiPending = true
      const message = this.tStatic('askCaiForSupportMessageTemplate').replace('{search_term}', this.lastSearchQuery)
      this.publish('chat.open', message)
    }
  }
}
</script>

<style lang="scss">
    .instant-result {
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      display: flex;
      flex-direction: column;
      width: 100%;
      max-height: calc(100vh - 7.5rem);
      background-color: color(background-white);
      border-radius: 0 0 other(border-radius) other(border-radius);
      box-shadow: 0 0 5px 0 rgba(119, 119, 119, .5);
      padding: measure(half);
      color: color(text);
      overflow-y: auto;

      @include media(small) {
        flex-direction: row;
        flex-wrap: wrap;
      }

      @include media(medium) {
        top: 4.5rem;
        right: initial;
        max-height: calc(86vh - 4.5rem);
      }
    }

    .instant-result__no-results-search {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      min-height: 3rem;
      padding: measure(eighth) measure(quarter);
      text-align: center;
      flex-direction: column;
    }

    .instant-result__no-results-search-description {
        border: 1px solid #44B1FA;
        padding: 0.6rem;
        margin-top: .5rem;
        max-width: 40rem;
    }

    .instant-result__did-you-mean {
      display: flex;
      align-items: center;
      width: 100%;
      margin-bottom: measure(half);
    }

    .instant-result__did-you-mean-item {
      @include reset-link;
      @include body;
      display: flex;

      &::before {
        content: ',';
        display: inline-block;
        margin-right: measure(eighth);
      }

      &:first-child {
        margin-left: measure(eighth);

        &::before {
          display: none;
        }
      }

      &:hover {
        text-decoration: underline;
      }
    }

    .instant-result__products {
      margin-right: measure(full);
      margin-bottom: measure(half);

      @include media(small) {
        margin-bottom: 0;
      }
    }

    .instant-result__aside {
      flex-shrink: 0;

      @include media(small) {
        width: 20rem;
      }

      @include media(medium) {
        width: 28.125rem;
      }
    }

    .instant-result__aside-categories {
      margin-bottom: measure(half);
    }

    .instant-result__cai-divider {
      border: none;
      border-top: 1px solid color(gray-3);
      margin: measure(quarter) 0;
      width: 100%;
    }

    .instant-result__cai-row {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: measure(quarter);
      width: 100%;
      padding: measure(quarter) measure(half);
      background-color: color(blue-5);
      color: color(text-light);
      flex-wrap: wrap;
    }

    .instant-result__ask-cai-btn {
      display: inline-flex;
      align-items: center;
      gap: 0.375rem;
      flex-shrink: 0;
      padding: 0.5rem 1rem;
      height: 2.5rem;
      background-color: #fff;
      border: 1px solid #006db6;
      border-radius: 0.1875rem;
      font-size: 1rem;
      font-weight: 600;
      color: #006db6;
      cursor: pointer;
      white-space: nowrap;
      transition: background-color 0.2s ease;

      &:hover {
        background-color: #dfebf3;
        color: #006db6;
        text-decoration: none;
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }

    .instant-result__cai-status {
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
      font-size: 0.75rem;
      color: color(text-light);
    }
</style>
